import {
  Badge,
  Button,
  Group,
  Menu,
  Modal,
  Skeleton,
  Stack,
  Switch,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { useDisclosure } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { IconDots, IconEdit, IconPlayerPlay, IconShare, IconTrash } from '@tabler/icons-react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useAuth } from '~/Auth'
import { useDeletePlaylist, useUpdatePlaylist } from '~/api/mutations'
import { usePlaylist } from '~/api/queries'
import { DownloadButton } from '~/components/blocks/DownloadButton/DownloadButton'
import { EnqueueButton } from '~/components/blocks/EnqueueButton'
import { TrackTable } from '~/components/blocks/TrackTable/TrackTable'
import { trackToQueueItem } from '~/components/player/utils'
import { usePlayer } from '~/hooks/use-player'
import { useShowDownloads } from '~/hooks/use-show-downloads'
import { formatDurationLong } from '~/lib/format'
import { trackToDownloadMeta } from '~/lib/offline/downloads'

export const Route = createFileRoute('/_app/playlists/$playlistId')({
  component: PlaylistDetailPage,
})

interface EditFormValues {
  name: string
  comment: string
  public: boolean
}

function PlaylistDetailPage() {
  const { playlistId: playlistIdParam } = Route.useParams()
  const playlistId = Number(playlistIdParam)
  const navigate = useNavigate()
  const { data: playlist, isLoading } = usePlaylist(playlistId)
  const showDownloads = useShowDownloads()
  const deletePlaylist = useDeletePlaylist()
  const updatePlaylist = useUpdatePlaylist()
  const player = usePlayer()
  const { session } = useAuth()
  const currentUserId = session.data?.user?.id != null ? Number(session.data.user.id) : undefined
  const [editOpened, { open: openEdit, close: closeEdit }] = useDisclosure(false)
  const form = useForm<EditFormValues>({ initialValues: { name: '', comment: '', public: false } })

  if (isLoading) return <Skeleton h={400} />
  if (!playlist) return <Text>Playlist not found</Text>

  const isOwner = currentUserId != null && playlist.ownerId === currentUserId

  const playAll = () => {
    if (playlist.tracks.length > 0) {
      player.play(playlist.tracks.map(trackToQueueItem))
    }
  }

  const handleOpenEdit = () => {
    form.setValues({
      name: playlist.name,
      comment: playlist.comment ?? '',
      public: playlist.public,
    })
    openEdit()
  }

  const handleSave = async (values: EditFormValues) => {
    try {
      await updatePlaylist.mutateAsync({
        id: playlistId,
        name: values.name,
        comment: values.comment || undefined,
        public: values.public,
      })
      notifications.show({ title: 'Saved', message: 'Playlist updated', color: 'green' })
      closeEdit()
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to update', color: 'red' })
    }
  }

  const handleDelete = async () => {
    try {
      await deletePlaylist.mutateAsync(playlistId)
      notifications.show({ title: 'Deleted', message: 'Playlist deleted', color: 'green' })
      navigate({ to: '/playlists' })
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to delete', color: 'red' })
    }
  }

  return (
    <>
      <Modal opened={editOpened} onClose={closeEdit} title="Edit Playlist">
        <form onSubmit={form.onSubmit(handleSave)}>
          <Stack gap="sm">
            <TextInput label="Name" required {...form.getInputProps('name')} />
            <Textarea label="Comment" autosize minRows={2} {...form.getInputProps('comment')} />
            <Switch
              label="Public playlist"
              description="Anyone can view and listen to this playlist"
              {...form.getInputProps('public', { type: 'checkbox' })}
            />
            <Group justify="flex-end" mt="sm">
              <Button variant="subtle" onClick={closeEdit}>
                Cancel
              </Button>
              <Button type="submit" loading={updatePlaylist.isPending}>
                Save
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      <Stack gap="lg">
        <Group justify="space-between">
          <div>
            <Group gap="sm" align="center">
              <Title order={1}>{playlist.name}</Title>
              {!isOwner && (
                <Badge variant="light" color="blue">
                  Shared
                </Badge>
              )}
              {playlist.public && (
                <Badge variant="light" color="green" leftSection={<IconShare size={12} />}>
                  Public
                </Badge>
              )}
            </Group>
            {playlist.comment && <Text c="dimmed">{playlist.comment}</Text>}
            <Text size="sm" c="dimmed">
              {playlist.trackCount} tracks &middot; {formatDurationLong(playlist.duration)} &middot;
              by {playlist.owner}
            </Text>
          </div>
          <Group>
            <Button leftSection={<IconPlayerPlay size={16} />} onClick={playAll}>
              Play All
            </Button>
            {playlist.tracks.length > 0 && (
              <EnqueueButton tracks={playlist.tracks.map(trackToQueueItem)} />
            )}
            {showDownloads && playlist.tracks.length > 0 && (
              <DownloadButton variant="button" tracks={playlist.tracks.map(trackToDownloadMeta)} />
            )}
            {isOwner && (
              <Menu withinPortal position="bottom-end">
                <Menu.Target>
                  <Button variant="subtle">
                    <IconDots size={16} />
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item leftSection={<IconEdit size={14} />} onClick={handleOpenEdit}>
                    Edit
                  </Menu.Item>
                  <Menu.Divider />
                  <Menu.Item
                    color="red"
                    leftSection={<IconTrash size={14} />}
                    onClick={handleDelete}
                  >
                    Delete Playlist
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            )}
          </Group>
        </Group>

        {playlist.tracks.length > 0 ? (
          <TrackTable tracks={playlist.tracks} showNumber />
        ) : (
          <Text c="dimmed">This playlist is empty.</Text>
        )}
      </Stack>
    </>
  )
}
